import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { unitMapping } from '@sorare/core/src/lib/style';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BuyPackButton } from '#src/components/commonCard/BuyPackButton/index.tsx';
import { EligiblePlayers } from '#src/components/commonCard/BuyPacks/EligiblePlayers/index.tsx';

import { Ellipse } from './Ellipse/index.tsx';
import type { PackWithBuyButton_cardPack } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({
  center: true,
})`
  position: relative;
  isolation: isolate;
  will-change: transform;
  transition: transform 0.3s ease-in-out;
  &.scaleOnHover:hover,
  &.scaleOnHover:focus {
    transform: scale(1.02);
  }
`;

const Postionned = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  > svg {
    width: 100%;
    height: 100%;
  }
`;

const Relative = styled.div`
  position: relative;
  isolation: isolate;
`;

const PositionnedEligiblePlayers = styled.div`
  position: absolute;
  bottom: calc(-1 * var(--unit));
  right: calc(-1 * var(--quadruple-unit));
  left: calc(-1 * var(--quadruple-unit));
`;

const PackImg = styled(GlareEffect)`
  position: relative;
  object-fit: contain;
  height: 100%;
  width: calc(12 * var(--unit));
  @media ${mobileAndAbove} {
    width: calc(16 * var(--unit));
  }
  .isPremiumPack & {
    width: calc(18 * var(--unit));
    @media ${mobileAndAbove} {
      width: calc(20 * var(--unit));
    }
  }
`;

type Props = {
  cardPack: PackWithBuyButton_cardPack;
  cardPackGroupSlug: string;
  gap?: keyof typeof unitMapping;
  isPremiumPack?: boolean;
  scaleOnHover?: boolean;
  withEligiblePlayers?: boolean;
};
export const PackWithBuyButton = ({
  cardPack,
  cardPackGroupSlug,
  gap = 1,
  isPremiumPack = false,
  scaleOnHover = true,
  withEligiblePlayers = false,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const isGemsPack = cardPack?.currency === CardPackCurrency.COMMON_GEM;
  const commonPlayers = cardPack?.eligibleCommonPlayers;

  return (
    <Root
      center
      gap={gap}
      className={classNames({
        scaleOnHover,
        isPremiumPack,
      })}
    >
      {!!isPremiumPack && (
        <Postionned>
          <Ellipse color={cardPack.primaryColor} />
        </Postionned>
      )}
      <Relative>
        <Link
          to={`${generateSportContextPath(
            ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
            {
              params: {
                group: cardPackGroupSlug,
              },
            }
          )}?pack=${cardPack.slug}`}
        >
          <PackImg
            pictureUrl={proxyUrl(cardPack.pictureUrl, {
              cropWidth: CARD_SIZE,
            })}
          />
          {!!withEligiblePlayers && commonPlayers?.length > 0 && (
            <PositionnedEligiblePlayers>
              <EligiblePlayers
                commonPlayers={commonPlayers}
                color={cardPack?.primaryColor}
              />
            </PositionnedEligiblePlayers>
          )}
        </Link>
      </Relative>
      <BuyPackButton
        cardPack={cardPack}
        size="small"
        color={isGemsPack ? 'primary' : 'secondary'}
        confirmPurchaseColor={isGemsPack ? 'secondary' : 'primary'}
      />
    </Root>
  );
};

PackWithBuyButton.fragments = {
  cardPack: gql`
    fragment PackWithBuyButton_cardPack on CardPack {
      slug
      pictureUrl
      primaryColor: color(variant: PRIMARY)
      currency
      eligibleCommonPlayers {
        id
        ...EligiblePlayers_commonPlayer
      }
      ...BuyPackButton_cardPack
    }
    ${EligiblePlayers.fragments.commonPlayer}
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<PackWithBuyButton_cardPack>,
};
